/**
 * Camera Animation Utilities - Auto-zoom and smooth transitions for navigation
 * Waze-style camera behavior
 */

import type { MapRef } from 'react-map-gl';
import type { Coordinates } from '@/types';
import type { LngLatBoundsLike } from 'mapbox-gl';

interface CameraOptions {
  padding?: number;
  duration?: number;
  pitch?: number;
  bearing?: number;
}

/**
 * Calculate bounds from array of coordinates
 */
export function calculateBounds(coordinates: Coordinates[]): LngLatBoundsLike {
  if (coordinates.length === 0) {
    throw new Error('Need at least one coordinate to calculate bounds');
  }

  let minLng = coordinates[0].lng;
  let maxLng = coordinates[0].lng;
  let minLat = coordinates[0].lat;
  let maxLat = coordinates[0].lat;

  coordinates.forEach((coord) => {
    minLng = Math.min(minLng, coord.lng);
    maxLng = Math.max(maxLng, coord.lng);
    minLat = Math.min(minLat, coord.lat);
    maxLat = Math.max(maxLat, coord.lat);
  });

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/**
 * Zoom camera to fit route - Waze-style overview
 */
export function zoomToRoute(
  mapRef: MapRef | null,
  routeCoordinates: Coordinates[],
  options: CameraOptions = {}
): void {
  if (!mapRef || routeCoordinates.length === 0) return;

  const map = mapRef.getMap();
  if (!map) return;

  const bounds = calculateBounds(routeCoordinates);

  map.fitBounds(bounds, {
    padding: options.padding ?? 80,
    duration: options.duration ?? 1500,
    pitch: options.pitch ?? 0,
    bearing: options.bearing ?? 0,
  });
}

/**
 * Follow user position during navigation - tracking camera
 */
export function followUser(
  mapRef: MapRef | null,
  userPosition: Coordinates,
  heading?: number,
  options: CameraOptions = {}
): void {
  if (!mapRef) return;

  const map = mapRef.getMap();
  if (!map) return;

  map.easeTo({
    center: [userPosition.lng, userPosition.lat],
    zoom: 17, // Close zoom for turn-by-turn
    bearing: heading ?? 0, // Rotate map to match heading
    pitch: options.pitch ?? 50, // Tilt for 3D effect
    duration: options.duration ?? 1000,
  });
}

/**
 * Smooth transition to specific location
 */
export function flyTo(
  mapRef: MapRef | null,
  coordinates: Coordinates,
  zoom?: number,
  options: CameraOptions = {}
): void {
  if (!mapRef) return;

  const map = mapRef.getMap();
  if (!map) return;

  map.flyTo({
    center: [coordinates.lng, coordinates.lat],
    zoom: zoom ?? 15,
    bearing: options.bearing ?? 0,
    pitch: options.pitch ?? 0,
    duration: options.duration ?? 2000,
  });
}

/**
 * Reset camera to default view
 */
export function resetCamera(mapRef: MapRef | null): void {
  if (!mapRef) return;

  const map = mapRef.getMap();
  if (!map) return;

  map.easeTo({
    bearing: 0,
    pitch: 0,
    duration: 500,
  });
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 * Returns distance in meters
 */
export function calculateDistance(from: Coordinates, to: Coordinates): number {
  const R = 6371000; // Earth radius in meters
  const φ1 = (from.lat * Math.PI) / 180;
  const φ2 = (to.lat * Math.PI) / 180;
  const Δφ = ((to.lat - from.lat) * Math.PI) / 180;
  const Δλ = ((to.lng - from.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Calculate bearing (heading) from one point to another
 * Returns bearing in degrees (0-360)
 */
export function calculateBearing(from: Coordinates, to: Coordinates): number {
  const φ1 = (from.lat * Math.PI) / 180;
  const φ2 = (to.lat * Math.PI) / 180;
  const Δλ = ((to.lng - from.lng) * Math.PI) / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  const θ = Math.atan2(y, x);
  const bearing = ((θ * 180) / Math.PI + 360) % 360;

  return bearing;
}
